/**
 * Design Project Store
 *
 * This store manages design project data in memory with database persistence.
 * It implements the memory management functionality specified in SPEC_sync_design_project_into_memory.md.
 *
 * Key Features:
 * - Load design projects from database into memory
 * - Manage design project state and elements
 * - Handle project configuration updates
 * - Memory cleanup when switching projects
 *
 * Data Flow:
 * configTemplateId → Create Design Project → inProgressId → loadDesignProject() → DB → STORE
 */

import * as changeKeys from 'change-case/keys'
import { useDesignProjectService } from '@/services/design_project.service'

import type {
    DesignProject,
    ProductPart,
    PartElement,
    DesignProjectSelect,
    CreateDesignProjectParams,
    DesignProjectCreateBody,
    ConfigurationTemplate,
    DesignProjectInsert,
    DesignProjectPersonalizeRedirect,
} from '~~/layers/base/types/designProject'

import type {
    Color,
    Font,
    Liner,
    Monogram,
    Motif,
    Paper,
    Print,
} from '~~/layers/base/types/productPartConfiguration'
import { isStartPageWorkCancelled } from '~~/layers/base/utils/fetchAbort'

// Initialize the design project service for database operations
const {
    doGetDesignProjectById,
    doCreateNewDesignProject,
    doGetDesignProjectForPersonalize,
    doUpdateDesignProject,
} = useDesignProjectService()

// const runtimeConfig = useRuntimeConfig() as {
//     public?: {
//         NUXT_MY_APP_DESIGN_TOKEN_COOKIE?: string
//     }
// }

export const useDesignProjectStore = defineStore('designProject', () => {
    // ============================================================================
    // STATE VARIABLES
    // ============================================================================

    /**
     * Limits the amount of data we send to dataDog through the error tracker
     * Mask syntax: https://github.com/nemtsov/json-mask#readme
     */
    const ddErrorContextMask = ref(
        'partNames,designProjectElements,designProject(id,configurationCurrent(id,highLevelChoices,highLevelOptions,sku))'
    )

    /**
     * Main design project data loaded from database
     * Contains the complete design project configuration including parts, elements, and metadata
     */
    const designProject = ref<DesignProject | null>(null)

    /**
     * Alternative design project data structure
     * Used for creating new design projects from templates
     */
    const designProjectOne = ref<DesignProject | null>(null)

    /**
     * Flattened array of all design elements across all product parts
     * Used by UI components for rendering and interaction
     */
    const designProjectElements = ref<PartElement[]>([])

    /**
     * Array of part names from the current design project
     * Used by pricing components and other UI elements that need part names
     */
    const partNames = ref<string[]>([])

    /**
     * Array of part names from configurationCurrent.preview.parts
     * Used specifically by quantity selector component
     */
    const quantitySelectorPartNames = computed(() => {
        if (designProject.value?.configurationCurrent?.preview?.parts) {
            return designProject.value.configurationCurrent.preview.parts
                .map((part) => part.name)
                .filter((name) => name)
        }
        return []
    })

    /**
     * Total price for the current design project
     * Matches the total shown in the Add to Cart button
     * This value is updated via updateDesignProjectTotal() when pricing becomes available
     * Returns undefined if total is not available
     */
    const total = computed<number | undefined>(() => {
        if (!designProject.value?.total) {
            return undefined
        }
        const totalValue = parseFloat(designProject.value.total)
        return isNaN(totalValue) ? undefined : totalValue
    })

    // ============================================================================
    // MEMORY MANAGEMENT STATE VARIABLES (According to SPEC)
    // ============================================================================

    /**
     * Current design project identifier
     * Tracks the active inProgressId for the current design session
     * Used for database operations and state management
     */
    const inProgressId = ref<string | null>(null)

    /**
     * Redirect target after POST create (handwriting meta id, or off-the-shelf design_project id).
     */
    const lastCreatePersonalizeRedirect = ref<DesignProjectPersonalizeRedirect | null>(null)

    /**
     * Loading state indicator
     * Set to true when loading design project data from database
     * Used by UI components to show loading indicators
     */
    const isLoading = ref<boolean>(false)

    /**
     * Saving state indicator
     * Reserved for future auto-save functionality
     * Will be used to track when data is being saved to database
     */
    const isSaving = ref<boolean>(false)

    /**
     * Profile save state indicator
     * Indicates whether the current project has been saved to user profile
     * Used for authenticated users to track permanent saves
     */
    const isSavedToProfile = ref<boolean>(false)

    /**
     * Promise tracking the current in-flight save operation
     * Used to prevent duplicate save requests
     */
    let savePromise: Promise<void> | null = null

    /**
     * Hash of the last saved configuration
     * Used to determine if data has changed since the last save
     */
    let lastSavedHash: string | null = null

    // Retrieve the design token cookie name from the runtime config and access the corresponding cookie using `useAppCookie`.
    // The cookie is set with a maximum age of 1 day, allowing the app to read or update the design token consistently within that period.
    // const designTokenCookieName = runtimeConfig.public?.NUXT_MY_APP_DESIGN_TOKEN_COOKIE as string

    // const designTokenCookie = useAppCookie<string>(designTokenCookieName, {
    //     maxAge: 60 * 60 * 24 * 1,
    // })

    // ============================================================================
    // TRANSFORMATIONS
    // ============================================================================

    /**
     * Converts raw server data into client-friendly object.
     * Should be used on all data requested from server endpoints.
     * Converts all keys to camelCase.
     */
    const deserializeDesignProject = (
        data: DesignProjectSelect | null | undefined
    ): DesignProject | null => {
        if (!data) return null

        return changeKeys.camelCase(
            {
                id: data.id,
                configurationTemplateId: data.configuration_template_id,
                configurationTemplateCopy:
                    data.configuration_template_copy as ConfigurationTemplate,
                configurationCurrent: data.configuration_current as ConfigurationTemplate,
                userData: {
                    // TODO: add `guestId` and `guestName` when available
                    fingerprintId: data.fingerprint_id,
                    userId: data.user_id,
                },
                productData: {
                    sku: data.sku,
                },
                orderState: data.order_state,
                // TODO: map `pdfState` and `status`
                pdfState: '',
                status: '',
                state: data.design_project_state,
                parts: data.parts as ProductPart[],

                // Pricer data
                quantity: data.quantity,
                total: data.total,

                // User Images
                userImages: data.userImages,
            },
            99
        ) as DesignProject
    }

    /**
     * Takes client-friendly object and converts it into data ready
     * to be sent to the server. Should be used on all data being
     * passed to the server for creation specifically.
     *
     * NOTE: `configuration_current` and `configuration_template_copy`
     * are omitted from the return because the BE is expected to handle
     * requesting and sending the copy of the template data.
     */
    const serializeCreateDesignProject = (
        obj: CreateDesignProjectParams
    ): DesignProjectCreateBody => {
        const base: DesignProjectCreateBody = {
            fingerprint_id: obj.fingerprintId,
            user_id: obj.userId,
            sku: obj.sku,
            configuration_template_id: obj.configTemplateId,
            quantity: obj.productData?.quantity as number | undefined,
        }
        if (obj.type) base.type = obj.type
        if (obj.profileId) base.profile_id = obj.profileId
        if (obj.warehouseId) base.warehouse_id = obj.warehouseId
        if (obj.remainingStock != null) base.remaining_stock = obj.remainingStock
        if (obj.transactionType) base.transaction_type = obj.transactionType
        return base
    }

    /**
     * Takes client-friendly object and converts it into data ready
     * to be sent to the server for a PATCH update to the `configuration_current`
     * column. Also includes quantity and price totla and sets an auto-now
     * value for the `last_activity_at` column.
     */
    const serializePatchConfigCurrent = (
        obj: Pick<DesignProject, 'configurationCurrent' | 'quantity' | 'total'>
    ): Pick<
        DesignProjectInsert,
        'configuration_current' | 'quantity' | 'total' | 'last_activity_at'
    > => ({
        configuration_current: changeKeys.snakeCase(obj.configurationCurrent, 99),
        quantity: obj.quantity,
        total: obj.total,
        last_activity_at: new Date(),
    })

    // ============================================================================
    // MEMORY MANAGEMENT METHODS (According to SPEC)
    // ============================================================================

    /**
     * Load design project for personalize by type and id.
     * - type=purchase: id is design_project.id
     * - type=handwriting: id is handwriting_stationery.id
     */
    const loadDesignProjectForPersonalize = async (
        type: 'purchase' | 'handwriting',
        id: string
    ): Promise<void> => {
        const { data, error } = await doGetDesignProjectForPersonalize(type, id)
        if (error.value) throw error.value
        if (!data.value) return
        await loadDesignProject(data.value.id, data.value)
    }

    /**
     * Load design project from database into memory
     *
     * This is the core "DB → STORE" functionality specified in the spec.
     * Fetches design project data using the inProgressId and syncs it into the Pinia store.
     *
     * @param inProgressIdParam - The design project identifier from database
     * @param prefetched - Optional pre-fetched design project (avoids duplicate fetch)
     * @throws Error - If database fetch fails (will trigger redirect to PDP per spec)
     */
    const loadDesignProject = async (
        inProgressIdParam: string,
        prefetched?: DesignProjectSelect
    ): Promise<void> => {
        // Early return if already loaded or same id
        if (
            designProject.value?.id === inProgressIdParam ||
            inProgressId.value === inProgressIdParam
        ) {
            return
        }
        // Reset personalize form state when switching to a different design project
        else if (designProject.value?.id && designProject.value.id !== inProgressIdParam) {
            const personalizeFormStore = usePersonalizeFormStore()
            personalizeFormStore.resetPersonalizeFormState()
        }

        isLoading.value = true
        try {
            let raw: DesignProjectSelect | null | undefined
            if (prefetched) {
                raw = prefetched
            } else {
                const { data, error } = await doGetDesignProjectById(inProgressIdParam)
                if (error.value) throw error.value
                raw = data.value
            }
            if (!raw) throw new Error('No design project data')

            // Sync database data into store
            designProject.value = deserializeDesignProject(raw)

            inProgressId.value = inProgressIdParam

            // Set default quantity to first available quantity from priceVariants if not set
            // Note: adjustedPrices is derived from priceVariants, so quantities are the same
            if (designProject.value && !designProject.value.quantity) {
                const priceVariants =
                    designProject.value.configurationTemplateCopy?.priceVariants || []
                if (priceVariants.length > 0 && priceVariants[0]?.qty) {
                    const firstQty = Number(priceVariants[0].qty)
                    if (!isNaN(firstQty)) {
                        designProject.value.quantity = firstQty
                        // Trigger auto-save to persist the default quantity
                        triggerAutoSave()
                    }
                }
            }

            // Initialize the hash for the loaded configuration
            if (designProject.value) {
                const configJson = JSON.stringify({
                    configurationCurrent: designProject.value.configurationCurrent,
                    quantity: designProject.value.quantity,
                    total: designProject.value.total,
                })
                lastSavedHash = simpleHash(configJson)
            }

            // Extract and set design elements for UI components
            setDesignProjectElements(designProject.value!.parts)

            // Pricing is now handled by productPrice store which watches designProject changes
            // const magentoProductStore = useMagentoProductStore() old magentoStore
            const magentoProductStore = useAccsProductStore()
            magentoProductStore.loadMagentoProduct(designProject.value?.productData.sku || '')

            // Pre-fetch paper previews for all parts to use as placeholders
            // This happens asynchronously and doesn't block page load
            if (
                designProject.value?.productData?.sku &&
                designProject.value?.configurationCurrent?.baseStructure
            ) {
                const paperPreviewStore = usePaperPreviewStore()
                const sku = designProject.value.productData.sku
                const baseStructure = designProject.value.configurationCurrent.baseStructure

                // Get unique part names from baseStructure
                const uniquePartNames = new Set<string>()
                baseStructure.forEach((part) => {
                    if (part.name) {
                        uniquePartNames.add(part.name)
                    }
                })

                // Pre-fetch paper previews once for all parts (non-blocking)
                // This fetches both Card and Envelope previews in a single API call
                paperPreviewStore.fetchPaperPreviews(sku).catch((error) => {
                    // Silently fail - previews will just not be available as placeholders
                    // eslint-disable-next-line no-console
                    console.error(`Failed to pre-fetch paper previews for ${sku}:`, error)
                    // Log to DataDog for monitoring
                    const datadog = useDatadog()
                    const errorObj = error instanceof Error ? error : new Error(String(error))
                    datadog.logError(errorObj, {
                        context: 'paper_preview_prefetch',
                        sku,
                        message: `Failed to pre-fetch paper previews for ${sku}`,
                    })
                })
            }
        } finally {
            // Always reset loading state, even if error occurs
            isLoading.value = false
        }
    }

    /**
     * Clear all design project data from memory
     *
     * Used when switching between projects or resetting the design session.
     * Ensures clean state and prevents memory leaks.
     */
    const clearDesignProject = (): void => {
        designProject.value = null
        // designProjectOne.value = null
        designProjectElements.value = []
        inProgressId.value = null
        lastCreatePersonalizeRedirect.value = null
        isSavedToProfile.value = false
    }

    /**
     * Creates a new design project and assigns it as the current design
     * project in the store. Clears the current design project and all
     * related refs beforehand.
     */
    const createDesignProject = async (
        params: CreateDesignProjectParams,
        options?: { signal?: AbortSignal }
    ): Promise<void> => {
        clearDesignProject()
        const serializedParams = serializeCreateDesignProject(params)
        // TODO: need to test that this maintains stack trace, otherwise find a different method for errors
        const { data, error } = await doCreateNewDesignProject(serializedParams, options)
        if (error.value) {
            if (options?.signal && isStartPageWorkCancelled(options.signal, error.value)) {
                return
            }
            throw error.value
        }

        if (data.value?.personalizeRedirect) {
            lastCreatePersonalizeRedirect.value = data.value.personalizeRedirect
        }

        // If the API response contains a token, update the design token cookie with this value using `setIfExists`.
        // This ensures the cookie is only set if it already exists, keeping the token in sync without creating a new cookie unintentionally.
        // if (data?.value?.token) {
        //     designTokenCookie.setIfExists(data.value.token)
        // }

        designProject.value = deserializeDesignProject(data.value)
        inProgressId.value = designProject.value!.id

        // Set default quantity to first available quantity from priceVariants if not set
        // Note: adjustedPrices is derived from priceVariants, so quantities are the same
        if (designProject.value && !designProject.value.quantity) {
            const priceVariants = designProject.value.configurationTemplateCopy?.priceVariants || []
            if (priceVariants.length > 0 && priceVariants[0]?.qty) {
                const firstQty = Number(priceVariants[0].qty)
                if (!isNaN(firstQty)) {
                    designProject.value.quantity = firstQty
                    // Trigger auto-save to persist the default quantity
                    triggerAutoSave()
                }
            }
        }

        // Initialize the hash for the newly created configuration
        if (designProject.value) {
            const configJson = JSON.stringify({
                configurationCurrent: designProject.value.configurationCurrent,
                quantity: designProject.value.quantity,
                total: designProject.value.total,
            })
            lastSavedHash = simpleHash(configJson)
        }

        setDesignProjectElements(designProject.value?.parts || [])

        // Pricing is now handled by productPrice store which watches designProject changes
        // const magentoProductStore = useMagentoProductStore() old magentoStore

        const magentoProductStore = useAccsProductStore()
        magentoProductStore.loadMagentoProduct(designProject.value?.productData.sku || '')

        // Pre-fetch paper previews for all parts to use as placeholders
        // This happens asynchronously and doesn't block page load
        if (
            designProject.value?.productData?.sku &&
            designProject.value?.configurationCurrent?.baseStructure
        ) {
            const paperPreviewStore = usePaperPreviewStore()
            const sku = designProject.value.productData.sku
            const baseStructure = designProject.value.configurationCurrent.baseStructure

            // Get unique part names from baseStructure
            const uniquePartNames = new Set<string>()
            baseStructure.forEach((part) => {
                if (part.name) {
                    uniquePartNames.add(part.name)
                }
            })

            // Pre-fetch paper previews once for all parts (non-blocking)
            // This fetches both Card and Envelope previews in a single API call
            paperPreviewStore.fetchPaperPreviews(sku).catch((error) => {
                // Silently fail - previews will just not be available as placeholders
                // eslint-disable-next-line no-console
                console.error(`Failed to pre-fetch paper previews for ${sku}:`, error)
                // Log to DataDog for monitoring
                const datadog = useDatadog()
                const errorObj = error instanceof Error ? error : new Error(String(error))
                datadog.logError(errorObj, {
                    context: 'paper_preview_prefetch',
                    sku,
                    message: `Failed to pre-fetch paper previews for ${sku}`,
                })
            })
        }
    }

    /**
     * Auto-save design project current configuration to database
     *
     * Saves the current in-memory design project to the database.
     * This is called by update methods to persist changes.
     *
     * @throws Error - If save fails (will trigger redirect to PDP per spec)
     */
    const autoSaveToDatabase = async (): Promise<void> => {
        if (!inProgressId.value || !designProject.value) {
            // No project to save
            return
        }

        isSaving.value = true
        try {
            const { error } = await doUpdateDesignProject(
                inProgressId.value,
                serializePatchConfigCurrent(designProject.value)
            )
            if (error.value) {
                throw error.value
            }
        } finally {
            isSaving.value = false
        }
    }

    /**
     * Ensure design project is saved, waiting for any in-flight save to complete
     *
     * If a save is already in progress, waits for it to complete.
     * Otherwise, triggers a new save operation.
     * Useful for ensuring persistence before navigation or critical operations.
     *
     * @returns Promise that resolves when the save operation completes
     */
    const ensureDesignProjectSaved = async (): Promise<void> => {
        if (!inProgressId.value || !designProject.value) {
            // No project to save
            return
        }

        // Create a hash of the current configuration to detect changes
        const currentConfigJson = JSON.stringify({
            configurationCurrent: designProject.value.configurationCurrent,
            quantity: designProject.value.quantity,
            total: designProject.value.total,
        })
        const currentHash = simpleHash(currentConfigJson)

        // If there's already a save in progress, wait for it
        if (savePromise) {
            // Check if the data has changed since the save started
            if (currentHash === lastSavedHash) {
                // Data hasn't changed, return the existing promise
                return savePromise
            }
            // Data has changed, need to wait for current save then create new one
            await savePromise
        }

        // Update the hash to track what we're about to save
        lastSavedHash = currentHash

        // Create and track a new save operation
        savePromise = (async () => {
            try {
                await autoSaveToDatabase()
            } finally {
                // Clear the promise reference after completion
                savePromise = null
            }
        })()

        return savePromise
    }

    /**
     * Trigger auto-save in background
     *
     * Helper function to trigger auto-save without blocking the UI.
     * Handles errors separately to prevent UI blocking.
     */
    const triggerAutoSave = (): void => {
        ensureDesignProjectSaved().catch((error) => {
            // Handle auto-save errors separately (don't block UI)
            // eslint-disable-next-line no-console
            console.error('Auto-save failed:', error)
        })
    }

    /**
     * Update design project current configuration to database
     *
     * Saves the current in-memory design project to the database.
     * This is called by update methods to persist changes.
     *
     * @throws Error - If save fails (will trigger redirect to PDP per spec)
     */
    const updateDesignProjectConfigurationCurrent = (data: ConfigurationTemplate) => {
        if (!designProject.value) return

        designProject.value.configurationCurrent = data
        // Keep parts in sync with baseStructure so the store shows the same data everywhere
        if (data.baseStructure?.length) {
            designProject.value.parts = data.baseStructure
        }
        triggerAutoSave()
    }

    /**
     * Mark all parts as refreshed to trigger re-rendering of fabric-preview components
     *
     * This method toggles the isRefreshed flag for all parts in the baseStructure
     * to trigger re-rendering of the fabric-preview components in the UI.
     */
    const markAllPartsAsRefreshed = () => {
        if (!designProject.value?.configurationCurrent?.baseStructure) return

        designProject.value.configurationCurrent.baseStructure.forEach((part) => {
            part.isRefreshed = !part.isRefreshed // Toggle to trigger reactivity
        })
    }

    // ============================================================================
    // LEGACY METHODS (Maintained for backward compatibility)
    // ============================================================================

    /**
     * Initialize design project (legacy method)
     *
     * Wrapper around loadDesignProject for backward compatibility.
     * Used by existing components that expect this interface.
     *
     * @param projectId - The design project identifier
     */
    const initializeDesignProject = async ({ projectId }: { projectId: string | number }) => {
        await loadDesignProject(projectId.toString())
    }

    /**
     * Set design project elements from parts
     *
     * Extracts and flattens all elements from product parts into a single array.
     * Also extracts part names from configurationCurrent.baseStructure for use by pricing components.
     * Used by UI components for rendering and interaction.
     *
     * @param parts - Array of product parts containing elements
     */
    const setDesignProjectElements = (parts: ProductPart[] | null) => {
        designProjectElements.value = []
        partNames.value = []

        if (parts && parts.length) {
            designProjectElements.value = parts.flatMap((p) => p.elements)
        }

        // Extract part names from configurationCurrent.baseStructure
        if (designProject.value?.configurationCurrent?.baseStructure) {
            partNames.value = designProject.value.configurationCurrent.baseStructure
                .map((part) => part.name)
                .filter((name) => name)
        }
    }

    // ============================================================================
    // DESIGN CONFIGURATION UPDATE METHODS
    // ============================================================================

    /**
     * Update color configuration across all design elements
     *
     * Applies color changes to all relevant elements in the design project.
     * Updates both individual elements and part-level express configurations.
     * Triggers auto-save to persist changes to database.
     *
     * @param color - The color configuration to apply
     * @throws Error - If auto-save fails (will trigger redirect to PDP per spec)
     */
    const updateDesignProjectColorDetails = async ({ color }: { color: Color }) => {
        if (!designProject.value) {
            throw new Error('No design project loaded')
        }

        const parts = designProject.value.parts
        if (parts && parts.length) {
            for (let p = 0; p < parts.length; p++) {
                const currentPart = parts[p]
                const allElememts = currentPart?.elements
                if (allElememts?.length) {
                    for (let e = 0; e < allElememts.length; e++) {
                        if (!['paper'].includes(allElememts[e]!.type)) {
                            if (allElememts[e]!.color) allElememts[e]!.color = color
                        }
                    }
                }
                if (currentPart?.express?.color && Array.isArray(currentPart.express.color)) {
                    const isDuplicate = currentPart.express.color.some(
                        (item: Color) => item.cid === color.cid
                    )
                    if (!isDuplicate) {
                        currentPart.express.color.push(color)
                    }
                } else currentPart!.express!.color = color
            }
        }
        setDesignProjectElements(designProject.value.parts)

        // Trigger auto-save in background
        triggerAutoSave()
    }

    /**
     * Update motif configuration across all design elements
     *
     * Applies motif changes to motif-type elements in the design project.
     * Triggers auto-save to persist changes to database.
     *
     * @param motif - The motif configuration to apply (undefined means remove)
     * @throws Error - If auto-save fails (will trigger redirect to PDP per spec)
     */
    const updateDesignProjectMotifDetails = async ({ motif }: { motif: Motif | undefined }) => {
        if (!designProject.value) {
            throw new Error('No design project loaded')
        }

        const parts = designProject.value.parts
        if (parts && parts.length) {
            for (let p = 0; p < parts.length; p++) {
                const currentPart = parts[p]
                const allElements = currentPart?.elements
                if (allElements?.length) {
                    for (let e = 0; e < allElements.length; e++) {
                        if (['motif'].includes(allElements[e]!.type)) {
                            if (motif) {
                                // Setting a motif - clear removed flag and set value
                                allElements[e]!.removed = false
                                allElements[e]!.value = motif
                            } else {
                                // Removing motif - set removed flag but keep existing value
                                allElements[e]!.removed = true
                                // Keep the existing value intact - don't modify it
                                // The crane-pricer will skip this element based on the removed flag
                            }
                        }
                    }
                }
            }
        }
        setDesignProjectElements(designProject.value.parts)

        // Save the updated configurationCurrent to database
        updateDesignProjectConfigurationCurrent(designProject.value.configurationCurrent)

        // Pricing is now handled by productPrice store which watches designProject changes
    }

    /**
     * Update monogram configuration across all design elements
     *
     * Applies monogram changes to monogram-type elements in the design project.
     * Triggers auto-save to persist changes to database.
     *
     * @param monogram - The monogram configuration to apply
     * @throws Error - If auto-save fails (will trigger redirect to PDP per spec)
     */
    const updateDesignProjectMonogramDetails = async ({ monogram }: { monogram: Monogram }) => {
        if (!designProject.value) {
            throw new Error('No design project loaded')
        }

        const parts = designProject.value.parts
        if (parts && parts.length) {
            for (let p = 0; p < parts.length; p++) {
                const currentPart = parts[p]
                const allElememts = currentPart?.elements
                if (allElememts?.length) {
                    for (let e = 0; e < allElememts.length; e++) {
                        if (['monogram'].includes(allElememts[e]!.type)) {
                            if (allElememts[e]!.value) allElememts[e]!.value = monogram
                        }
                    }
                }
                // code will be use in future

                // if (currentPart?.express?.monogram && Array.isArray(currentPart.express.monogram)) {
                //     const isDuplicate = currentPart.express.monogram.some(
                //         (item: Monogram) => item.id === monogram.id
                //     )
                //     if (!isDuplicate) {
                //         currentPart.express.monogram.push(monogram)
                //     }
                // } else currentPart!.express!.monogram = monogram
            }
        }
        setDesignProjectElements(designProject.value.parts)

        // Trigger auto-save in background
        triggerAutoSave()
    }

    /**
     * Update paper configuration at product level
     *
     * Applies paper changes to the product-level express configuration.
     * Triggers auto-save to persist changes to database.
     *
     * @param paper - The paper configuration to apply
     * @throws Error - If auto-save fails (will trigger redirect to PDP per spec)
     */
    const updateDesignProjectPaperDetails = async ({ paper }: { paper: Paper }) => {
        if (!designProject.value) {
            throw new Error('No design project loaded')
        }

        // update current paper at Product level
        if (designProject.value?.express) {
            const expressObject = designProject.value?.express
            if (expressObject?.paper && Array.isArray(expressObject.paper)) {
                const isDuplicate = expressObject.paper.some((item: Paper) => item.id === paper.id)
                if (!isDuplicate) {
                    expressObject.paper.push(paper)
                }
            } else expressObject.paper = paper
        }

        // Trigger auto-save in background
        triggerAutoSave()
    }

    /**
     * Updates a paper selection in the base structure
     * Note: Envelope matching happens in generateConfigurationTemplate.ts script, not in the UI
     * @param partIndex - Index of the part in baseStructure
     * @param paperValue - The selected paper object
     * @param isEnvelope - Whether this is an envelope paper selection (for logging/debugging only, does NOT trigger sync)
     */
    const updatePaperSelection = async (
        partIndex: number,
        paperValue: Paper,
        _isEnvelope: boolean = false
    ): Promise<void> => {
        if (!designProject.value) {
            throw new Error('No design project loaded')
        }

        const baseStructure = designProject.value.configurationCurrent?.baseStructure
        if (!baseStructure || !baseStructure[partIndex]) {
            throw new Error(`Invalid part index: ${partIndex}`)
        }

        const part = baseStructure[partIndex]
        if (!part.elements) {
            throw new Error(`Part at index ${partIndex} has no elements`)
        }

        // Find the paper element in this part
        const paperElement = part.elements.find((el) => el.type === 'paper')
        if (!paperElement) {
            throw new Error(`No paper element found in part at index ${partIndex}`)
        }

        // Update the paper element value in baseStructure
        paperElement.value = paperValue
        // Update the part's paper field to match the selected paper name
        ;(part as ProductPart).paper = paperValue.name

        // Also update the corresponding part in the parts array to keep them in sync
        // Find the matching part by name since indices may not align
        if (designProject.value.parts) {
            const partInParts = designProject.value.parts.find((p) => p.name === part.name)
            if (partInParts) {
                if (partInParts.elements) {
                    const paperElementInParts = partInParts.elements.find(
                        (el) => el.type === 'paper'
                    )
                    if (paperElementInParts) {
                        paperElementInParts.value = paperValue
                    }
                }
                // Update the part's paper field in the parts array as well
                partInParts.paper = paperValue.name
            }
        }

        // Note: Envelope matching happens in generateConfigurationTemplate.ts script, not in the UI
        // The UI does NOT automatically sync envelope papers when card papers change

        // Update parts array to reflect changes
        setDesignProjectElements(designProject.value.parts)

        // Update configurationCurrent to trigger reactivity
        updateDesignProjectConfigurationCurrent(designProject.value.configurationCurrent)

        // Mark all parts as refreshed to trigger preview updates
        markAllPartsAsRefreshed()

        // Pricing is now handled by productPrice store which watches designProject changes
    }

    /**
     * Update liner configuration across all design elements
     *
     * Applies liner changes to liner-type elements in the design project.
     * Triggers auto-save to persist changes to database.
     *
     * @param liner - The liner configuration to apply
     * @throws Error - If auto-save fails (will trigger redirect to PDP per spec)
     */
    const updateDesignProjectLinerDetails = async ({ liner }: { liner: Liner }) => {
        if (!designProject.value) {
            throw new Error('No design project loaded')
        }

        const parts = designProject.value.parts
        if (parts && parts.length) {
            for (let p = 0; p < parts.length; p++) {
                const currentPart = parts[p]
                const allElememts = currentPart?.elements
                if (allElememts?.length) {
                    for (let e = 0; e < allElememts.length; e++) {
                        if (['liner'].includes(allElememts[e]!.type)) {
                            allElememts[e]!.value = liner
                        }
                    }
                }
                if (currentPart?.express?.liner) {
                    currentPart.express.liner = liner
                }
            }
        }
        setDesignProjectElements(designProject.value.parts)

        // Trigger auto-save in background
        triggerAutoSave()
    }

    /**
     * Update font configuration across all design elements
     *
     * Applies font changes to all relevant elements in the design project.
     * Updates both individual elements and part-level express configurations.
     * Triggers auto-save to persist changes to database.
     *
     * @param font - The font configuration to apply
     * @throws Error - If auto-save fails (will trigger redirect to PDP per spec)
     */
    const updateDesignProjectFontDetails = async ({ font }: { font: Font }) => {
        if (!designProject.value) {
            throw new Error('No design project loaded')
        }

        const parts = designProject.value.parts
        if (parts && parts.length) {
            for (let p = 0; p < parts.length; p++) {
                const currentPart = parts[p]
                const allElememts = currentPart?.elements
                if (allElememts?.length) {
                    for (let e = 0; e < allElememts.length; e++) {
                        if (!['paper'].includes(allElememts[e]!.type)) {
                            if ('font' in allElememts[e]!) {
                                allElememts[e]!.font = font
                            }
                        }
                    }
                }
                if (currentPart?.express?.font && Array.isArray(currentPart.express.font)) {
                    const isDuplicate = currentPart.express.font.some(
                        (item: Font) => item.id === font.id
                    )
                    if (!isDuplicate) {
                        currentPart.express.font.push(font)
                    }
                } else currentPart!.express!.font = font
            }
        }
        setDesignProjectElements(designProject.value.parts)

        // Trigger auto-save in background
        triggerAutoSave()
    }

    /**
     * Update print configuration across all design elements
     *
     * Applies print changes to all relevant elements in the design project.
     * Updates both individual elements and part/product-level express configurations.
     *
     * @param print - The print configuration to apply
     */
    const updateDesignProjectQuantity = async (quantity: number) => {
        if (designProject.value) {
            designProject.value.quantity = quantity
            await ensureDesignProjectSaved()
        }
    }

    /**
     * Update total price for the current design project
     *
     * Updates the total price in the design project when pricing becomes available.
     * This should be called from pricing stores/composables when the total is calculated.
     * The total is stored as a string to preserve decimal precision.
     *
     * @param total - The total price as a number or string (string preserves precision)
     */
    const updateDesignProjectTotal = (total: number | string | undefined): void => {
        if (!designProject.value) {
            return
        }

        // Skip update if total is undefined or the same as current total
        if (total === undefined) {
            return
        }

        // Convert to string for precision (matches database format)
        const totalString = typeof total === 'number' ? total.toString() : total

        // Skip update if the total hasn't changed
        if (designProject.value.total === totalString) {
            return
        }

        designProject.value.total = totalString

        // Trigger auto-save in background
        triggerAutoSave()
    }

    const updateDesignProjectPrintDetails = async ({ print }: { print: Print }) => {
        const parts = designProject.value?.parts
        if (parts && parts.length) {
            for (let p = 0; p < parts.length; p++) {
                const currentPart = parts[p]
                const allElememts = currentPart?.elements
                if (allElememts?.length) {
                    for (let e = 0; e < allElememts.length; e++) {
                        if (!['paper'].includes(allElememts[e]!.type)) {
                            if ('print' in allElememts[e]!) {
                                allElememts[e]!.print = print
                            }
                        }
                    }
                }
                // update print at part level
                if (currentPart?.express?.print && Array.isArray(currentPart.express.print)) {
                    const isDuplicate = currentPart.express.print.some(
                        (item: Print) => item.processId === print.processId
                    )
                    if (!isDuplicate) {
                        currentPart.express.print.push(print)
                    }
                } else currentPart!.express!.print = print
            }
        }
        // update current print at Product level
        if (designProject.value?.express) {
            const expressObject = designProject.value?.express
            if (expressObject?.print && Array.isArray(expressObject.print)) {
                const isDuplicate = expressObject.print.some(
                    (item: Print) => item.processId === print.processId
                )
                if (!isDuplicate) {
                    expressObject.print.push(print)
                }
            } else expressObject.print = print
        }
        setDesignProjectElements(designProject.value!.parts)

        // Trigger auto-save in background
        triggerAutoSave()
    }

    /**
     * Auto-save design project current configuration to database
     *
     * Saves the current in-memory design project to the database.
     * This is called by update methods to persist changes.
     *
     * @throws Error - If save fails (will trigger redirect to PDP per spec)
     */
    /**
     * Assigns a user to the currently in-progress design project and persists this change to the database.
     *
     * The function first checks if there is an active project (`inProgressId`) and a loaded design project;
     * if not, it exits early since there is nothing to update.
     * It then calls `doUpdateDesignProject` to update the project with the provided `userId`, the existing `fingerprintId` from the project data,
     * and the current timestamp as `last_activity_at` to track the most recent activity.
     * If the API response includes a new token, it updates the design token cookie using `setIfExists` to keep the client in sync.
     * Any errors during the update could be thrown (currently commented out), which would trigger the redirect to the product page per specification.
     */
    const assignUserToDesignProject = async (userId: string): Promise<void> => {
        if (!inProgressId.value || !designProject.value) {
            // No project to save
            return
        }

        if (!userId) {
            // No user id to patch
            return
        }

        // Call the API function to update the design project with the new data
        await doUpdateDesignProject(inProgressId.value, {
            user_id: userId, // ID of the current user performing the update
            // fingerprint_id: designProject?.value?.userData?.fingerprintId, // Optional fingerprint ID, commented out for now
            last_activity_at: new Date(), // Update the timestamp for last activity
        })

        // Optional: save new token if returned by API
        // if (data?.value?.token) {
        //     designTokenCookie.setIfExists(data.value.token)
        // }

        // If there was an error returned by the API, throw it so it can be caught by the caller
    }

    // ============================================================================
    // STORE EXPORTS
    // ============================================================================

    return {
        ddErrorContextMask,

        // Core design project data
        designProject,
        designProjectOne,
        designProjectElements,
        partNames,
        quantitySelectorPartNames,
        total,

        // Memory management state variables (According to SPEC)
        inProgressId,
        lastCreatePersonalizeRedirect,
        isLoading,
        isSaving,
        isSavedToProfile,

        // Memory management methods (According to SPEC)
        loadDesignProject,
        loadDesignProjectForPersonalize,
        clearDesignProject,
        createDesignProject,
        autoSaveToDatabase,
        ensureDesignProjectSaved,
        triggerAutoSave,

        // set user id in design project
        assignUserToDesignProject,
        // Legacy methods (Backward compatibility)
        initializeDesignProject,
        updateDesignProjectColorDetails,
        updateDesignProjectFontDetails,
        updateDesignProjectPaperDetails,
        updateDesignProjectMotifDetails,
        updateDesignProjectMonogramDetails,
        updateDesignProjectLinerDetails,
        updateDesignProjectPrintDetails,
        updateDesignProjectConfigurationCurrent,
        updateDesignProjectQuantity,
        updateDesignProjectTotal,
        markAllPartsAsRefreshed,

        // Paper selection methods
        updatePaperSelection,
    }
})
